import { useState, useMemo, useCallback } from 'react';
import { format, subDays, subMonths, subYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subWeeks } from 'date-fns';
import { trendsService } from '../services/api';

export const useTrendsData = () => {
  const [trendData, setTrendData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTrendsData = useCallback(async (fromDate, toDate, countries = [], profile = '') => {
    try {
      setIsLoading(true);
      setError(null);

      const formattedFromDate = format(fromDate, 'dd/MM/yyyy');
      const formattedToDate = format(toDate, 'dd/MM/yyyy');

      const data = await trendsService.getTrendsData(formattedFromDate, formattedToDate, countries, profile);

      const processedData = data.trends.map(item => {
        const date = new Date(item.date);
        const count = item.count || (item.successfulDiets + item.dietsWithWarning + item.noDiets);
        
        return {
          date: item.date,
          fullDate: date,
          count: count,
          successfulDiets: item.successfulDiets,
          dietsWithWarning: item.dietsWithWarning,
          noDiets: item.noDiets,
          displayDate: format(date, 'MMM dd'),
          period: 'daily'
        };
      });

      // Extract countries from API response
      const apiCountries = data.countries || [];
      const processedCountries = apiCountries.map(country => ({
        id: country.key,
        name: country.value
      }));

      setTrendData(processedData);
      setCountries(processedCountries);
      setProfiles(data.profiles || []);
    } catch (err) {
      console.error('Error fetching trends data:', err);
      setError(err.message);
      setTrendData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    trendData,
    countries,
    profiles,
    isLoading,
    error,
    fetchTrendsData
  };
};

export const getPresetDateRange = (period) => {
  const today = new Date();
  let startDate, endDate;

  switch (period) {
    case '30D':
      startDate = subDays(today, 29);
      endDate = today;
      break;
    case '180D':
      startDate = subDays(today, 179);
      endDate = today;
      break;
    case '365D':
      startDate = subDays(today, 364);
      endDate = today;
      break;
    case 'THIS_WEEK':
      startDate = startOfWeek(today, { weekStartsOn: 1 }); // Monday start
      endDate = today;
      break;
    case 'THIS_MONTH':
      startDate = startOfMonth(today);
      endDate = today;
      break;
    case 'THIS_YEAR':
      startDate = startOfYear(today);
      endDate = today;
      break;
    case 'PREV_WEEK': {
      const prevWeek = subWeeks(today, 1);
      startDate = startOfWeek(prevWeek, { weekStartsOn: 1 });
      endDate = endOfWeek(prevWeek, { weekStartsOn: 1 });
      break;
    }
    case 'PREV_MONTH': {
      const prevMonth = subMonths(today, 1);
      startDate = startOfMonth(prevMonth);
      endDate = endOfMonth(prevMonth);
      break;
    }
    case 'PREV_YEAR': {
      const prevYear = subYears(today, 1);
      startDate = startOfYear(prevYear);
      endDate = endOfYear(prevYear);
      break;
    }
    default:
      startDate = subDays(today, 29);
      endDate = today;
  }

  return { fromDate: startDate, toDate: endDate };
};

const aggregateToWeekly = (dailyData) => {
  const weeklyGroups = new Map();

  dailyData.forEach(day => {
    const weekStart = startOfWeek(day.fullDate, { weekStartsOn: 1 });
    const weekKey = format(weekStart, 'yyyy-MM-dd');

    if (!weeklyGroups.has(weekKey)) {
      weeklyGroups.set(weekKey, {
        date: weekKey,
        fullDate: weekStart,
        count: 0,
        successfulDiets: 0,
        dietsWithWarning: 0,
        noDiets: 0,
        displayDate: format(weekStart, 'MMM dd'),
        period: 'weekly',
        periodLabel: `Week of ${format(weekStart, 'MMM dd')}`
      });
    }

    const week = weeklyGroups.get(weekKey);
    week.count += day.count;
    week.successfulDiets += day.successfulDiets;
    week.dietsWithWarning += day.dietsWithWarning;
    week.noDiets += day.noDiets;
  });

  return Array.from(weeklyGroups.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
};

const aggregateToMonthly = (dailyData) => {
  const monthlyGroups = new Map();

  dailyData.forEach(day => {
    const monthStart = startOfMonth(day.fullDate);
    const monthKey = format(monthStart, 'yyyy-MM-dd');

    if (!monthlyGroups.has(monthKey)) {
      monthlyGroups.set(monthKey, {
        date: monthKey,
        fullDate: monthStart,
        count: 0,
        successfulDiets: 0,
        dietsWithWarning: 0,
        noDiets: 0,
        displayDate: format(monthStart, 'MMM yyyy'),
        period: 'monthly'
      });
    }

    const month = monthlyGroups.get(monthKey);
    month.count += day.count;
    month.successfulDiets += day.successfulDiets;
    month.dietsWithWarning += day.dietsWithWarning;
    month.noDiets += day.noDiets;
  });

  return Array.from(monthlyGroups.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const useProcessedTrendData = (trendData, currentAggregation) => {
  return useMemo(() => {
    if (!trendData.length) return [];
    
    switch (currentAggregation) {
      case 'weekly':
        return aggregateToWeekly(trendData);
      case 'monthly':
        return aggregateToMonthly(trendData);
      case 'daily':
      default:
        return trendData;
    }
  }, [trendData, currentAggregation]);
};